import { and, asc, eq, gt, inArray, isNull, lt, ne, or } from 'drizzle-orm'

import type { Database } from '../client'
import type { InsertPremium, SelectMembership } from '../schema'
import type { DatabaseTransaction } from './child-merge'
import { InvoiceSeatAssignment, member, Membership, Premium, SeatEntitlement } from '../schema'

/**
 * Fulfilling a purchased seat pool: the Membership, its seats, and the buyer's chosen children.
 *
 * Runs in ONE transaction, which is why it lives here and not in the `core / new-membership` trigger
 * task: that task holds the neon-http handle, whose `.transaction()` throws at runtime. Call this
 * with `dbPool`. Locally both handles are node-postgres, so a caller passing `db` passes every local
 * run and fails only after deploy — see `client.ts`.
 */

export interface MintPurchasedSeatsArgs {
  invoiceId: string
  organizationId: string
  seats: number
  startDate: Date
  endDate: Date
}

export interface MintPurchasedSeatsResult {
  membership: SelectMembership
  seatsCreated: number
  /** Children stamped onto a minted seat, in seat order. */
  assignedChildIds: string[]
  /** Children the buyer chose at checkout who no longer qualify (left the school, or seated since). */
  skippedChildIds: string[]
  /** This organization's free-trial seats released so their holders could move to a paid seat. */
  releasedTrialSeatIds: string[]
}

/**
 * Create a purchased pool, mint its seats, and stamp the buyer's chosen children onto them.
 *
 * The Membership insert is INSIDE the transaction on purpose. `Membership.invoiceId` has no unique
 * index, so the trigger's replay guard is a read-then-write check. With two separate commits, a
 * crash between them left a membership with zero seats, and every later retry then short-circuited
 * on the membership it found and returned success without minting anything. One transaction makes
 * that state unreachable: either nothing exists for the invoice, or the pool and all its seats do.
 *
 * Not idempotent on its own — a second call for the same invoice mints a second pool. The caller's
 * "is there already a Membership for this invoice" check is what makes retries safe.
 *
 * Order matters and is fixed by the sequence of awaited statements below: each seated child's
 * free-trial seat from THIS organization is released (`childId = NULL`) before their paid seat is
 * inserted. `no_overlapping_child_seats` (an EXCLUDE over `child_id` and the seat window, partial on
 * `child_id IS NOT NULL`) is checked per statement, so a released seat is out of its scope by the
 * time the insert runs. The release is unconditional, not driven by the constraint: `startDate` may
 * be a future `customMembershipStartDate` that never overlaps the trial seat, and the child is still
 * moving to a paid seat, so the trial seat is still freed.
 */
export async function mintPurchasedSeats(
  database: Database,
  { invoiceId, organizationId, seats, startDate, endDate }: MintPurchasedSeatsArgs,
): Promise<MintPurchasedSeatsResult> {
  if (!Number.isInteger(seats) || seats < 1) {
    throw new Error(`Cannot mint ${seats} seats for invoice ${invoiceId}: seats must be a positive integer`)
  }

  return database.transaction((tx) =>
    mintPoolInTransaction(tx, { invoiceId, organizationId, seats, startDate, endDate }),
  )
}

/**
 * The body of {@link mintPurchasedSeats}, on a transaction the caller already holds.
 *
 * Split out so a RENEWAL can mint its pool AND the add-on chain its empty seats ride in ONE
 * transaction (`mintRenewalSeats`): the trigger's replay guard reads "is there a Membership for this
 * invoice", so a pool committed without its chain would look fulfilled forever.
 *
 * `seats` may be ZERO here, and only here. A renewal whose every bought seat is an empty one — the
 * school continues nobody and parks seats for students it has not enrolled — still needs the pool
 * row: it is the anchor the next renewal continues from and the window the chain's last segment
 * rides. A zero-seat pool mints no `Premium` rows of its own; every seat read counts rows, never
 * `membership.seats`, so it reads as what it is.
 */
export async function mintPoolInTransaction(
  tx: DatabaseTransaction,
  { invoiceId, organizationId, seats, startDate, endDate }: MintPurchasedSeatsArgs,
): Promise<MintPurchasedSeatsResult> {
  if (!Number.isInteger(seats) || seats < 0) {
    throw new Error(`Cannot mint ${seats} seats for invoice ${invoiceId}: seats must be a non-negative integer`)
  }

  // 1. The pool. First statement, so a failure anywhere below rolls it back too.
  const [membership] = await tx
    .insert(Membership)
    .values({ invoiceId, organizationId, seats, startDate, endDate })
    .returning()
  if (!membership) throw new Error(`Failed to create membership for invoice ${invoiceId}`)

  if (seats === 0) {
    return { membership, seatsCreated: 0, assignedChildIds: [], skippedChildIds: [], releasedTrialSeatIds: [] }
  }

  // 2. Who the buyer chose, re-checked against the window being minted.
  const { assignable, skipped } = await resolveAssignableChildren(tx, {
    invoiceId,
    organizationId,
    seats,
    startDate,
    endDate,
  })

  // 3. Release BEFORE stamping. Awaited to completion before step 4 issues its insert.
  const releasedTrialSeatIds = await releaseOrgTrialSeats(tx, { organizationId, childIds: assignable })

  // 4. Mint. `Array.from` with a fresh object per slot — `new Array(n).fill(obj)` puts ONE shared
  //    object in every slot, invisible while all rows are identical and "every seat goes to the
  //    same child" the moment `childId` differs.
  const rows: InsertPremium[] = Array.from({ length: seats }, (_, index) => ({
    organizationId,
    source: 'membership' as const,
    startDate,
    endDate,
    membershipId: membership.id,
    childId: assignable[index] ?? null,
  }))
  // Bare `.returning()`: `tx` is a union of the pooled and neon-http transaction types, and a
  // field-projecting `.returning(fields)` cannot be resolved across that union (TS2554).
  const created = await tx.insert(Premium).values(rows).returning()

  return {
    membership,
    seatsCreated: created.length,
    assignedChildIds: assignable,
    skippedChildIds: skipped,
    releasedTrialSeatIds,
  }
}

export interface ResolveAssignableChildrenArgs {
  invoiceId: string
  organizationId: string
  seats: number
  startDate: Date
  endDate: Date
}

export interface AssignableChildren {
  /** Children who still qualify, capped at `seats`, in a stable order. */
  assignable: string[]
  /** The rest of the checkout selection. */
  skipped: string[]
}

/**
 * Re-validate the checkout selection. `invoice_seat_assignment` rows are intent recorded at checkout;
 * between then and payment a student can leave the school or be seated by a parent. Ineligible
 * children are DROPPED, not fatal — the buyer paid, and the seats must exist either way.
 *
 * Mirrors the pre-flight check in `invoice.create` (`@package/api`) exactly, so the two layers cannot
 * disagree about who may be seated:
 *  - the child must still be a `member` of THIS organization;
 *  - the child must hold no seat OVERLAPPING the window being minted, other than a `free_trial`
 *    seat owned by THIS organization, which step 3 releases.
 *
 * Overlap, not "any live seat", and the difference is the renewal flow. A renewal mints a pool that
 * starts the day the current one ends, and every student continuing on it is sitting in a live
 * commercial seat right now — under the old `endDate > now` test the whole selection was dropped as
 * already-seated and the school paid for seats that came back empty. The test here is the same one
 * the `no_overlapping_child_seats` EXCLUDE constraint enforces, half-open on both sides, so what
 * passes this check is exactly what the database will accept.
 *
 * A seat prepaid for a window that DOES overlap still blocks, which is what the old lower-bound-less
 * test was reaching for. Commercial seats (no entitlement), every other grant flavour, and any seat
 * owned by another organization all block on overlap.
 *
 * Takes the transaction handle, not a `Database`: these reads belong to the same transaction as the
 * writes that act on them.
 */
export async function resolveAssignableChildren(
  tx: DatabaseTransaction,
  { invoiceId, organizationId, seats, startDate, endDate }: ResolveAssignableChildrenArgs,
): Promise<AssignableChildren> {
  const intents = await tx
    .select({ childId: InvoiceSeatAssignment.childId })
    .from(InvoiceSeatAssignment)
    .where(eq(InvoiceSeatAssignment.invoiceId, invoiceId))
    // A stable order so the cap below picks the same children on every attempt.
    .orderBy(asc(InvoiceSeatAssignment.childId))

  const requested = intents.map((row) => row.childId)
  if (requested.length === 0) return { assignable: [], skipped: [] }

  const stillMembers = await tx
    .select({ userId: member.userId })
    .from(member)
    .where(and(eq(member.organizationId, organizationId), inArray(member.userId, requested)))
  const memberIds = new Set(stillMembers.map((row) => row.userId))

  const blocked = await tx
    .select({ childId: Premium.childId })
    .from(Premium)
    .leftJoin(SeatEntitlement, eq(SeatEntitlement.id, Premium.entitlementId))
    .where(
      and(
        inArray(Premium.childId, requested),
        // Half-open overlap, `[startDate, endDate)` against `[start_date, end_date)` — a seat
        // ending exactly when this one begins does not overlap, which is what makes a renewal's
        // back-to-back seat legal both here and at the EXCLUDE constraint.
        gt(Premium.endDate, startDate),
        lt(Premium.startDate, endDate),
        or(
          isNull(SeatEntitlement.sourceType),
          ne(SeatEntitlement.sourceType, 'free_trial'),
          ne(Premium.organizationId, organizationId),
        ),
      ),
    )
  const blockedIds = new Set(blocked.map((row) => row.childId).filter((id): id is string => id !== null))

  const assignable = requested.filter((id) => memberIds.has(id) && !blockedIds.has(id)).slice(0, seats)
  const assignableIds = new Set(assignable)
  const skipped = requested.filter((id) => !assignableIds.has(id))

  return { assignable, skipped }
}

export interface ReleaseOrgTrialSeatsArgs {
  organizationId: string
  childIds: string[]
}

/**
 * Release THIS organization's live free-trial seats held by `childIds`, so each child can take a paid
 * seat. Returns the released seat ids.
 *
 * The seat row survives, unassigned, still in the trial pool — reusable for another student until the
 * trial expires. Never delete a seat: `reading_session.premium_id` is ON DELETE RESTRICT, and royalty
 * pays out per `count(distinct premium_id)`.
 *
 * Takes the transaction handle, not a `Database`: this write must land before the paid-seat insert in
 * the same transaction, and a `Database`-typed handle would compile and then run outside it.
 */
export async function releaseOrgTrialSeats(
  tx: DatabaseTransaction,
  { organizationId, childIds }: ReleaseOrgTrialSeatsArgs,
): Promise<string[]> {
  if (childIds.length === 0) return []

  const trialSeats = await tx
    .select({ id: Premium.id })
    .from(Premium)
    .innerJoin(SeatEntitlement, eq(SeatEntitlement.id, Premium.entitlementId))
    .where(
      and(
        inArray(Premium.childId, childIds),
        eq(Premium.organizationId, organizationId),
        gt(Premium.endDate, new Date()),
        eq(SeatEntitlement.sourceType, 'free_trial'),
      ),
    )
  const trialSeatIds = trialSeats.map((row) => row.id)
  if (trialSeatIds.length === 0) return []

  const released = await tx
    .update(Premium)
    .set({ childId: null })
    .where(inArray(Premium.id, trialSeatIds))
    // Bare `.returning()` for the same union-typed `tx` reason as the seat insert above.
    .returning()

  return released.map((row) => row.id)
}
